import type { WindowEntry, WindowVertex } from '@roomtreatment/core';
import {
  roundSnapM,
  syncWindowGeometry,
  windowPolygon,
} from '@roomtreatment/core';
import { pointInPolygon, clampPointToPolygon, type PolyPt } from './polygonClamp';
import { clampWindow, surfaceKey, type SurfaceFrame } from './panelSurface';

/**
 * Default opening: ⅓ of the host width × ¼ of the host height, snapped to 5 cm.
 * Doors reuse this exact module (see `defaultDoor` in `doorOpening.ts`, and
 * `syncDoorGeometry` in `packages/core/src/doorGeom.ts` for the sill=0
 * invariant) — same (u,v) stamp, not a new geometry class.
 */
export function defaultWindow(
  frame: SurfaceFrame,
  existing: WindowEntry[],
): Omit<WindowEntry, 'id'> {
  const w = Math.max(0.2, Math.min(roundSnapM(frame.uMax / 3), frame.uMax));
  const h = Math.max(0.2, Math.min(roundSnapM(frame.vMax / 4), frame.vMax));
  const v = frame.ref.kind === 'ceiling'
    ? frame.vMax / 2
    : clampNum(0.9 + h / 2, h / 2, frame.vMax - h / 2);
  const base = syncWindowGeometry({
    surface: surfaceKey(frame.ref),
    v,
    w,
    h,
    u: frame.uMax / 2,
  });
  const step = w + 0.4;
  for (let k = 0; k < 8; k++) {
    for (const dir of k === 0 ? [0] : [1, -1]) {
      const cand = clampWindowOpening(frame, {
        ...base,
        u: frame.uMax / 2 + dir * k * step,
      });
      if (!existing.some((e) => openingsOverlap(e, cand))) return cand;
    }
  }
  return clampWindowOpening(frame, { ...base, u: frame.uMax / 2 });
}

/** True when every vertex and edge midpoint of the opening lies inside `host`. */
export function openingContainedInHost(
  host: PolyPt[],
  win: Pick<WindowEntry, 'u' | 'v' | 'w' | 'h' | 'polygon'>,
): boolean {
  if (host.length < 3) return false;
  const verts = windowPolygon(win);
  const inside = (p: { u: number; v: number }) => pointInPolygon({ x: p.u, y: p.v }, host);
  if (!verts.every(inside)) return false;
  for (let i = 0; i < verts.length; i++) {
    const a = verts[i]!;
    const b = verts[(i + 1) % verts.length]!;
    if (!inside({ u: (a.u + b.u) / 2, v: (a.v + b.v) / 2 })) return false;
  }
  return true;
}

function clampTranslationToHost(
  host: PolyPt[],
  verts: WindowVertex[],
  du: number,
  dv: number,
): { du: number; dv: number } {
  const moved = (t: number) => verts.map((p) => ({
    u: p.u + du * t,
    v: p.v + dv * t,
  }));
  const fits = (t: number) => openingContainedInHost(host, {
    u: 0, v: 0, w: 0, h: 0, polygon: moved(t),
  });
  if (fits(1)) return { du, dv };
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (fits(mid)) lo = mid;
    else hi = mid;
  }
  return { du: du * lo, dv: dv * lo };
}

function clampNum(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

function toPoly(verts: readonly WindowVertex[]): PolyPt[] {
  return verts.map((p) => ({ x: p.u, y: p.v }));
}

function segmentsCross(
  a: WindowVertex, b: WindowVertex, c: WindowVertex, d: WindowVertex,
): boolean {
  const den = (b.u - a.u) * (d.v - c.v) - (b.v - a.v) * (d.u - c.u);
  if (Math.abs(den) < 1e-12) return false;
  const t = ((c.u - a.u) * (d.v - c.v) - (c.v - a.v) * (d.u - c.u)) / den;
  const s = ((c.u - a.u) * (b.v - a.v) - (c.v - a.v) * (b.u - a.u)) / den;
  return t > 1e-9 && t < 1 - 1e-9 && s > 1e-9 && s < 1 - 1e-9;
}

export function openingsOverlap(
  a: Pick<WindowEntry, 'u' | 'v' | 'w' | 'h' | 'polygon'>,
  b: Pick<WindowEntry, 'u' | 'v' | 'w' | 'h' | 'polygon'>,
): boolean {
  const gapU = (a.w + b.w) / 2 - Math.abs(a.u - b.u);
  const gapV = (a.h + b.h) / 2 - Math.abs(a.v - b.v);
  if (gapU <= 0.05 || gapV <= 0.05) return false;
  const pa = windowPolygon(a);
  const pb = windowPolygon(b);
  const polyA = toPoly(pa);
  const polyB = toPoly(pb);
  if (pa.some((p) => pointInPolygon({ x: p.u, y: p.v }, polyB))) return true;
  if (pb.some((p) => pointInPolygon({ x: p.u, y: p.v }, polyA))) return true;
  for (let i = 0; i < pa.length; i++) {
    const a0 = pa[i]!;
    const a1 = pa[(i + 1) % pa.length]!;
    for (let j = 0; j < pb.length; j++) {
      if (segmentsCross(a0, a1, pb[j]!, pb[(j + 1) % pb.length]!)) return true;
    }
  }
  return false;
}

/** Keep every vertex inside the host surface. AABB is rebuilt from the polygon. */
export function clampWindowOpening<T extends Omit<WindowEntry, 'id'>>(
  frame: SurfaceFrame,
  win: T,
  host?: PolyPt[],
): T {
  const hostPoly = host && host.length >= 3 ? host : null;
  const poly = windowPolygon(win).map((p) => {
    if (!hostPoly) {
      return {
        u: clampNum(p.u, 0, frame.uMax),
        v: clampNum(p.v, 0, frame.vMax),
      };
    }
    const q = clampPointToPolygon({ x: p.u, y: p.v }, hostPoly);
    return { u: q.x, v: q.y };
  });
  const boxed = clampWindow(frame, syncWindowGeometry({ ...win, polygon: poly }));
  return syncWindowGeometry({ ...win, ...boxed, polygon: poly });
}

export function containedInFrame(
  frame: SurfaceFrame,
  win: Pick<WindowEntry, 'u' | 'v' | 'w' | 'h' | 'polygon'>,
  host?: PolyPt[],
): boolean {
  if (host && host.length >= 3) return openingContainedInHost(host, win);
  return windowPolygon(win).every(
    (p) => p.u >= -1e-6 && p.u <= frame.uMax + 1e-6
      && p.v >= -1e-6 && p.v <= frame.vMax + 1e-6,
  );
}

/** Rigid translate of the opening outline in the host (u, v) frame. */
export function translateOpening<T extends Omit<WindowEntry, 'id'>>(
  win: T,
  du: number,
  dv: number,
): T {
  const poly = windowPolygon(win).map((p) => ({ u: p.u + du, v: p.v + dv }));
  return syncWindowGeometry({ ...win, polygon: poly });
}

/**
 * Slide an opening on its host wall. Vertices stay inside the frame (rigid
 * first, then per-vertex clamp if the opening is larger than the wall).
 * Returns null when the result would overlap another opening on this surface.
 */
export function tryMoveWindow<T extends Omit<WindowEntry, 'id'>>(
  frame: SurfaceFrame,
  win: T,
  du: number,
  dv: number,
  others: Pick<WindowEntry, 'u' | 'v' | 'w' | 'h' | 'polygon'>[],
  host?: PolyPt[],
): T | null {
  const poly = windowPolygon(win);
  const hostPoly = host && host.length >= 3 ? host : null;

  if (hostPoly) {
    const { du: adjU, dv: adjV } = clampTranslationToHost(hostPoly, poly, du, dv);
    const moved = clampWindowOpening(frame, translateOpening(win, adjU, adjV), hostPoly);
    if (others.some((o) => openingsOverlap(o, moved))) return null;
    return moved;
  }

  let minU = Infinity;
  let maxU = -Infinity;
  let minV = Infinity;
  let maxV = -Infinity;
  for (const p of poly) {
    minU = Math.min(minU, p.u + du);
    maxU = Math.max(maxU, p.u + du);
    minV = Math.min(minV, p.v + dv);
    maxV = Math.max(maxV, p.v + dv);
  }
  let adjU = du;
  let adjV = dv;
  if (minU < 0) adjU += -minU;
  if (maxU > frame.uMax) adjU -= maxU - frame.uMax;
  if (minV < 0) adjV += -minV;
  if (maxV > frame.vMax) adjV -= maxV - frame.vMax;
  const moved = clampWindowOpening(frame, translateOpening(win, adjU, adjV));
  if (others.some((o) => openingsOverlap(o, moved))) return null;
  return moved;
}
